// shadcn/ui component: https://ui.shadcn.com/docs/components/radix/select
import React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/component/ds/utils';
import { DsThemedPortal } from 'src/component/ds/DsRoot';

/*
 * "Select & Combobox - Vega" in Figma. Like Dialog, the content portals to
 * document.body and so re-applies DS_ROOT_CLASS.
 */

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

const selectTriggerVariants = cva(
  [
    'ds-flex ds-w-full ds-items-center ds-justify-between ds-rounded',
    'ds-border ds-border-input ds-bg-background ds-shadow-2xs',
    'ds-text-foreground [&>span]:ds-truncate',
    'data-[placeholder]:ds-text-muted-foreground',
    'focus-visible:ds-outline-none focus-visible:ds-shadow-chemelex-focus',
    'disabled:ds-cursor-default disabled:ds-opacity-50',
    'aria-[invalid=true]:ds-border-destructive aria-[invalid=true]:ds-shadow-none',
    'aria-[invalid=true]:focus-visible:ds-shadow-chemelex-focus-error'
  ],
  {
    variants: {
      size: {
        xs: 'ds-min-h-5.5 ds-pl-2 ds-pr-1.5 ds-py-[3px] ds-gap-1 ds-text-xs ds-leading-4',
        sm: 'ds-min-h-8 ds-pl-2.5 ds-pr-2 ds-py-1.5 ds-gap-1.5 ds-text-sm',
        default: 'ds-min-h-9 ds-pl-2.5 ds-pr-2 ds-py-2 ds-gap-1.5 ds-text-sm',
        lg: 'ds-min-h-10 ds-pl-2.5 ds-pr-2 ds-py-2.5 ds-gap-1.5 ds-text-sm'
      }
    },
    defaultVariants: { size: 'default' }
  }
);

export interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
  VariantProps<typeof selectTriggerVariants> {}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, children, size, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(selectTriggerVariants({ size }), className)}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <KeyboardArrowDownIcon sx={{ fontSize: 16 }} className='ds-shrink-0 ds-text-muted-foreground' aria-hidden='true' />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('ds-flex ds-items-center ds-justify-center ds-py-1', className)}
    {...props}
  >
    <KeyboardArrowUpIcon sx={{ fontSize: 16 }} aria-hidden='true' />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('ds-flex ds-items-center ds-justify-center ds-py-1', className)}
    {...props}
  >
    <KeyboardArrowDownIcon sx={{ fontSize: 16 }} aria-hidden='true' />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <DsThemedPortal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        className={cn(
          'ds-relative ds-z-50 ds-max-h-96 ds-min-w-[8rem] ds-overflow-hidden',
          'ds-rounded ds-border ds-border-border ds-bg-popover ds-text-popover-foreground',
          'ds-shadow-md',
          'data-[state=open]:ds-animate-in data-[state=closed]:ds-animate-out',
          'data-[state=open]:ds-fade-in-0 data-[state=closed]:ds-fade-out-0',
          position === 'popper' && 'data-[side=bottom]:ds-translate-y-1 data-[side=top]:-ds-translate-y-1',
          className
        )}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'ds-p-1',
            position === 'popper' && 'ds-h-[var(--radix-select-trigger-height)] ds-w-full ds-min-w-[var(--radix-select-trigger-width)]'
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </DsThemedPortal>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('ds-px-2 ds-py-1.5 ds-text-xs ds-font-medium ds-text-muted-foreground', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'ds-relative ds-flex ds-w-full ds-cursor-default ds-select-none ds-items-center',
      'ds-rounded ds-py-1.5 ds-pl-2 ds-pr-8 ds-text-sm ds-outline-none',
      'focus:ds-bg-accent focus:ds-text-accent-foreground',
      'data-[disabled]:ds-pointer-events-none data-[disabled]:ds-opacity-50',
      className
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <span className='ds-absolute ds-right-2 ds-flex ds-h-3.5 ds-w-3.5 ds-items-center ds-justify-center'>
      <SelectPrimitive.ItemIndicator>
        <CheckIcon sx={{ fontSize: 16 }} aria-hidden='true' />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-ds-mx-1 ds-my-1 ds-h-px ds-bg-border', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  selectTriggerVariants,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton
};
